/**
 * Authentication Store
 * 
 * Zustand store for authentication state management
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authAPI } from '../services/api'
import { getTabClinic, setTabClinic, clearTabClinic } from '../lib/tabClinic'

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      clinics: [],
      currentClinicId: null,
      isLoading: false,
      isAuthenticated: false,
      isImpersonating: false,

      // Actions
      setLoading: (isLoading) => set({ isLoading }),

      setAuthData: ({ user, token, clinics, currentClinicId, isImpersonating = false }) => {
        if (!user) {
          console.error('Auth Store: setAuthData called with undefined user!')
          return
        }
        
        // Ensure token is stored in localStorage
        if (token) {
          localStorage.setItem('auth-token', token)
        }
        
        set({
          user: {
            ...user,
            fullName: `${user.firstName} ${user.lastName}` // Add fullName getter
          },
          token,
          clinics,
          currentClinicId,
          isImpersonating,
          isAuthenticated: true,
          isLoading: false,
        })
        setTabClinic(currentClinicId)
      },

      clearAuth: () => {
        // Clear token from localStorage
        localStorage.removeItem('auth-token')
        clearTabClinic()
        
        set({
          user: null,
          token: null,
          clinics: [],
          currentClinicId: null,
          isAuthenticated: false,
          isImpersonating: false,
          isLoading: false,
        })
      },

      setCurrentClinic: (clinicId) => {
        setTabClinic(clinicId)
        set({ currentClinicId: clinicId })
      },

      // Initialize auth state from stored token
      initialize: async () => {
        set({ isLoading: true })
        
        // First check if we have a token from localStorage or persisted state
        const { token } = get()
        const localToken = localStorage.getItem('auth-token')
        const effectiveToken = token || localToken
        
        if (!effectiveToken) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const response = await authAPI.me(effectiveToken)
          // Extract from response.data.data (backend wraps in data object)
          const { user, currentClinicId, clinics, isImpersonating } = response.data.data || response.data

          set({
            user: {
              ...user,
              fullName: `${user.firstName} ${user.lastName}` // Add fullName getter
            },
            token: effectiveToken, // Ensure token is set in state
            clinics,
            currentClinicId,
            isImpersonating,
            isAuthenticated: true,
            isLoading: false,
          })
          // /me answered for this tab's clinic when it had one (X-Clinic-Id),
          // otherwise for the token's; either way that is now this tab's.
          setTabClinic(currentClinicId)
          
          // Ensure token is also in localStorage
          if (!localStorage.getItem('auth-token')) {
            localStorage.setItem('auth-token', effectiveToken)
          }
        } catch (error) {
          // This tab was on a clinic the account no longer holds: forget it and
          // start again on the token's clinic rather than signing out.
          if (error.response?.data?.error?.code === 'CLINIC_ACCESS_DENIED' && getTabClinic() !== undefined) {
            clearTabClinic()
            return get().initialize()
          }
          console.error('Auth initialization failed:', error)
          // Clear invalid token from both state and localStorage
          localStorage.removeItem('auth-token')
          clearTabClinic()
          set({
            user: null,
            token: null,
            clinics: [],
            currentClinicId: null,
            isAuthenticated: false,
            isImpersonating: false,
            isLoading: false,
          })
        }
      },

      // Login action
      login: async (credentials) => {
        set({ isLoading: true })

        try {
          const response = await authAPI.login(credentials)

          // Extract from response.data.data (backend wraps in data object)
          const { user, token, clinics, currentClinicId, redirectTo } = response.data.data

          get().setAuthData({
            user,
            token,
            clinics,
            currentClinicId,
          })

          // Return redirectTo for tv_display users (backend sends '/tv')
          return { success: true, data: response.data.data, redirectTo }
        } catch (error) {
          console.error('Login failed:', error.response?.data?.error?.message || error.message)
          set({ isLoading: false })
          const errorMessage = error.response?.data?.error?.message || 'Login failed'
          return { success: false, error: errorMessage }
        }
      },

      // Logout action
      logout: async () => {
        const { token } = get()
        
        try {
          if (token) {
            await authAPI.logout(token)
          }
        } catch (error) {
          console.error('Logout API call failed:', error)
          // Continue with local logout even if API fails
        } finally {
          get().clearAuth()
        }
      },

      // Refresh token action
      refreshToken: async () => {
        const { token } = get()
        
        if (!token) return false

        try {
          const response = await authAPI.refresh(token)
          const newToken = response.data.token

          set({ token: newToken })
          return true
        } catch (error) {
          console.error('Token refresh failed:', error)
          get().clearAuth()
          return false
        }
      },

      // Switch clinic context
      switchClinic: async (clinicId) => {
        const { user, token, clinics } = get()
        
        if (!user || !token) {
          throw new Error('Not authenticated')
        }

        // Handle "all" as null (for "All Clinics" option)
        const targetClinicId = clinicId === 'all' || clinicId === '' ? null : clinicId

        // Validate clinic access if a specific clinic is selected
        if (targetClinicId) {
          const clinic = clinics.find(c => c.id === targetClinicId)
          if (!clinic && user.role !== 'super_admin') {
            throw new Error('Access denied to clinic')
          }
          if (!clinic) {
            throw new Error('Clinic not found')
          }
        }

        try {
          // Call the backend to get a new token with updated clinic context
          const response = await authAPI.switchClinic(targetClinicId)
          const { token: newToken, currentClinicId } = response.data.data
          
          // Ensure token is stored in localStorage immediately
          if (newToken) {
            localStorage.setItem('auth-token', newToken)
          }
          
          // Update both the token and clinic ID. The new token makes this the
          // clinic a NEW tab opens on; this tab's own choice is setTabClinic.
          setTabClinic(currentClinicId)
          set({ 
            token: newToken, 
            currentClinicId: currentClinicId 
          })
        } catch (error) {
          console.error('Failed to switch clinic on backend:', error)
          throw new Error('Failed to switch clinic')
        }
        
        return true
      },

      // Set token from OAuth callback and fetch user data
      setTokenAndFetchUser: async (token) => {
        set({ isLoading: true })
        
        try {
          // Store token. A fresh sign-in starts on the token's clinic, not on
          // whatever this tab was showing before.
          localStorage.setItem('auth-token', token)
          clearTabClinic()
          set({ token })
          
          // Fetch user data using the token
          const response = await authAPI.me(token)
          const { user, currentClinicId, clinics, isImpersonating } = response.data.data || response.data

          set({
            user: {
              ...user,
              fullName: `${user.firstName} ${user.lastName}`
            },
            token,
            clinics,
            currentClinicId,
            isImpersonating: isImpersonating || false,
            isAuthenticated: true,
            isLoading: false,
          })
          setTabClinic(currentClinicId)
          
          return { success: true }
        } catch (error) {
          console.error('Failed to fetch user after OAuth:', error)
          localStorage.removeItem('auth-token')
          set({
            user: null,
            token: null,
            clinics: [],
            currentClinicId: null,
            isAuthenticated: false,
            isLoading: false,
          })
          throw error
        }
      },

      // Get current user info
      getCurrentUser: () => {
        const { user } = get()
        return user
      },

      // Check if user has specific role
      hasRole: (role) => {
        const { user } = get()
        return user?.role === role
      },

      // Check if user can access clinic
      canAccessClinic: (clinicId) => {
        const { user, clinics } = get()
        
        if (!user) return false
        if (user.role === 'super_admin') return true
        
        return clinics.some(clinic => clinic.id === clinicId)
      },

      // Get available clinics for current user
      getAvailableClinics: () => {
        const { clinics } = get()
        return clinics
      },

      // Check if currently impersonating
      getImpersonationStatus: () => {
        const { isImpersonating, user } = get()
        return {
          isImpersonating,
          impersonatedUser: isImpersonating ? user : null,
        }
      },
    }),
    {
      name: 'auth-storage', // name of the item in localStorage
      // The persisted clinic is whichever tab wrote last; a tab that has its
      // own (lib/tabClinic) keeps it across reloads.
      merge: (persisted, current) => {
        const tabClinic = getTabClinic()
        return {
          ...current,
          ...persisted,
          ...(tabClinic !== undefined ? { currentClinicId: tabClinic } : {}),
        }
      },
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        clinics: state.clinics,
        currentClinicId: state.currentClinicId,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
)

export { useAuthStore }
